import { CdkDragDrop, CdkDragEnd, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { HamAccordionItemComponent } from '@modeso/aumico-components';
import {
    AumicoEditableInformationPopupComponent,
    IDialogDataWithAction,
    IPopupData,
    MAT_DIALOG_CUSTOM_CLASS,
    OkCancelDialogActions,
} from '@modeso/aumico-lib-core-fe';
import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import {
    IAccountMappingTemplate,
    INotesComponent,
    INotesSection,
    ISuperTableComponent,
    ItemType,
    Notes,
} from '@modeso/types__aumico-lib-annualreport-be';
import {
    ComponentType,
    INotesOfAnnualReportDTO,
    IRichText,
    ISuperTableOptions,
} from '@modeso/types__aumico-lib-notes-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep, isEqual, omit } from 'lodash-es';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ProjectToCopyFrom } from '../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/project-to-copy-from.interface';
import { selectActiveProjectId } from '../../../../../aumico-lib-projects-fe/src/lib/reducers/projects-overview.reducer';
import { getNotesTemplateById } from '../../actions/notesTemplates.actions';
import { setNotesAccordionOpenState } from '../../actions/ui.actions';
import { ConfirmationModal } from '../../components/confirmation-modal/confirmation-modal.component';
import { INotesComponentsListDelegation } from '../../components/notes-components-list/INotesComponentsListDelegation.interface';
import { ReferencingNotesComponent, ReferencingNotesComponentData } from '../../components/referencing-notes/referencing-notes.component';
import { Errors } from '../../enums/errors.enum';
import { AccordionCompletenessHelper } from '../../helpers/accordionCompleteness.helper';
import { MockNotesAccordionDataHelper } from '../../helpers/mockNotesAccordionData.helper';
import { NotesNumberingHelper } from '../../helpers/notes-numbering.helper';
import { NotesHelper } from '../../helpers/notes.helper';
import { NotesAccordionDataSettingsUpdaterHelper } from '../../helpers/notesAccordionDataSettingsUpdater.helper';
import { NotesAutoFillHelper } from '../../helpers/notesAutoFill.helper';
import { NotesLiveRefreshHelper } from '../../helpers/notes-live-refresh.helper';
import { getAllFlis } from '../../helpers/notesCopy.helper';
import { SuperTableHelper } from '../../helpers/super-table.helper';
import { AddNoteAccordionResult } from '../../interfaces/add-note-accordion-result.interface';
import { IAnnualReportAccordionsData } from '../../interfaces/IAnnualReportAccordionsData.interface';
import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { IFxNodeStamp, IFxNotesContext } from '../../interfaces/IFxNotesContext.interface';
import { IFLIForNotes } from '../../interfaces/IFLIForNotes.interface';
import { MappingItemsSelectionNode } from '../../interfaces/mapping-items-selection-node.interface';
import { INotesComponentsNumberedTitles } from '../../interfaces/notes-numbered-titles.interface';
import { INotesSectionViewModel } from '../../interfaces/NotesSectionViewModel.interface';
import { INotesTemplateByLegalFormAndAccountBasisRequest } from '../../interfaces/requests/INotesTemplateByLegalFormAndAccountBasisRequest.interface';
import { SuperTableMapper } from '../../mappers/super-table.mapper';
import { NotesProvider } from '../../provider/notes.provider';
import { selectAccordionOpenState } from '../../reducers/ui.reducer';
import { NoteComponentViewState } from '../../types/NoteComponentViewState.types';
import { INotesDelegate } from './INotesDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesPage');

@UntilDestroy()
@Component({
    selector: 'aumico-notes-page',
    templateUrl: 'notes.page.html',
    styleUrls: ['notes.page.scss'],
})
export class NotesPage implements OnInit, OnChanges, INotesComponentsListDelegation, AfterViewInit {
    @ViewChild('menuFlyout') menuFlyout!: ElementRef;

    dialogData: { projects: ProjectToCopyFrom[] };
    selectedAnnualReportIdToCopyNotesStructureFrom: string;
    private _notesToBeAutoFilled: IAnnualReportAccordionsData;
    private _notesFromChosenAnnualReport: IAnnualReportAccordionsData;
    private _annualReportDefaultSettings: IAnnualReportDefaultSettings;
    @Input() public set injectedProviderSetter(injectedProvider: NotesProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Input() isNotesStructureToCopyFromAnotherAnnualReportNull = false;

    isCopyNotesStructureFromAnotherAnnualReportButtonClicked = false;

    @Output() copyNotesStructureFromAnotherAnnualReportButtonClicked: EventEmitter<void> = new EventEmitter();

    @Input() isNotesSavedBefore = false;
    @Input() isVersionMismatch = false;
    /** REV-22548: a sealed report's notes are never refreshed, so leaving them can never trigger a save. */
    @Input() isSealed = false;
    @Input() combination: INotesTemplateByLegalFormAndAccountBasisRequest;
    @Input() arTemplateNotesTemplateId: string | undefined;

    annualReportAccordions: IAnnualReportAccordionsData | undefined;
    @Input() set annualReportAccordionsData(value: IAnnualReportAccordionsData) {
        this.annualReportAccordions = cloneDeep(value);
        if (this.annualReportAccordions != null) {
            this.notesAccordionData = this.notesAccordionDataSettingsUpdaterHelper.updateCurrencyAndDatesOfAccordions(
                this.annualReportAccordions.accordions,
                this.annualReportDefaultSettings,
                this.flisWithAccounts,
            );

            this.outputtedAnnualAccordionsData = cloneDeep<IAnnualReportAccordionsData>(this.annualReportAccordions);

            if (this.notesToBeAutoFilled != null) {
                this.notesAccordionData = cloneDeep<IAnnualReportAccordionsData>(
                    this.notesHelper.addPreviousYearDataToNotesAccordionComponents(
                        this.notesAccordionData,
                        this.notesToBeAutoFilled.accordions,
                    ),
                );
            }


            this.updateAccordionsArray();

            const isAccordionChildrenEmptyCurrently: boolean = this.accordionChildren.toArray().length !== 0;

            if (isAccordionChildrenEmptyCurrently) {
                this.accordions = this.accordionChildren.toArray();
                this.addAccordionsMarkers();
            }
        } else if (this.isNotesStructureToCopyFromAnotherAnnualReportNull) {
            //Initialize accordions first
            this.initAccordions();
        }

        this.resetState = Array(this.notesAccordionData.length).fill(false);

        this.createNotesNumberingObject();

        if (this.DELEGATE != null) {
            this.DELEGATE.outputtedAccordionsData(
                this.outputtedAnnualAccordionsData,
                this.resetState.filter((state) => state === true).length > 0,
            );
        }
    }

    @Input()
    get notesToBeAutoFilled(): IAnnualReportAccordionsData {
        return this._notesToBeAutoFilled;
    }

    set notesToBeAutoFilled(value: IAnnualReportAccordionsData) {
        this._notesToBeAutoFilled = cloneDeep<IAnnualReportAccordionsData>(value);

        if (this.annualReportAccordions != null && this.notesToBeAutoFilled != null) {
            this.notesAccordionData = cloneDeep<IAnnualReportAccordionsData>(
                this.notesHelper.addPreviousYearDataToNotesAccordionComponents(
                    this.notesAccordionData,
                    this.notesToBeAutoFilled.accordions,
                ),
            );
        }

        this.handleNotesTypeOfNotesTemplateWithAutofill();
        this.createNotesNumberingObject();
    }

    previousYearNotesAccordions: INotesSection[] = []; // NOT USED FOR AUTOFILL. ONLY TO DISPLAY PREVIOUS YEAR DATA FOR SOME COMPONENTS.
    @Input() set previousYearNotes(value: IAnnualReportAccordionsData) {
        if (value != null && this.notesAccordionData != null && this.notesToBeAutoFilled == null) {
            this.previousYearNotesAccordions = cloneDeep<INotesSection[]>(value.accordions);

            this.notesAccordionData = cloneDeep<IAnnualReportAccordionsData>(
                this.notesHelper.addPreviousYearDataToNotesAccordionComponents(
                    this.notesAccordionData,
                    this.previousYearNotesAccordions,
                ),
            );
        }
    }

    @Input() set notesFromChosenAnnualReport(value: IAnnualReportAccordionsData) {
        this._notesFromChosenAnnualReport = cloneDeep<IAnnualReportAccordionsData>(value);
        this.annualReportAccordions = cloneDeep<IAnnualReportAccordionsData>(value);
        if (this.annualReportAccordions != null) {
            this.updateAccordionsWithDefaultSettings();
        }
    }

    flisWithAccounts: IFLIForNotes[] = [];
    @Input() set flis(value: IFLIForNotes[]) {
        this.flisWithAccounts = [...value];
    }
    /** FX (REV-21512): null on non-FX reports — components then behave exactly as today (G0). */
    @Input() fxNotesContext: IFxNotesContext | null = null;
    /** FX (REV-21512): per-account display values for the FLI tables, keyed `id|accountName`. */
    @Input() fliFxStamps: Map<string, IFxNodeStamp> | null = null;
    @Input() currentLanguage: string;

    @Input() languages: string[] = ['de-ch', 'en-us'];
    @Input() reportLocale: string;

    @Input()
    set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this._annualReportDefaultSettings = value;
        if (value != null && this.annualReportAccordions != null) {
            this.updateAccordionsWithDefaultSettings();
        }
    }

    get annualReportDefaultSettings(): IAnnualReportDefaultSettings {
        return this._annualReportDefaultSettings;
    }

    @Input() highlightTodos = true;
    @Output() highlightTodosChanged: EventEmitter<boolean> = new EventEmitter();

    DELEGATE: INotesDelegate;
    @Input() public set delegate(delegate: INotesDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }

        if (this.outputtedAnnualAccordionsData) {
            this.DELEGATE.outputtedAccordionsData(
                this.outputtedAnnualAccordionsData,
                this.resetState.filter((state) => state === true).length > 0,
            );
        }
    }

    @Input() set projectsList(value: ProjectToCopyFrom[]) {
        this.dialogData = {
            projects: cloneDeep<ProjectToCopyFrom[]>(value),
        };
    }

    @Input() activeAnnualReportEncryptedId: string | undefined;
    @Output() selectedAnnualReportIdToCopyNotesStructureFromEvent: EventEmitter<string> = new EventEmitter();

    @Input() supportOperation = false;

    @Input() balanceMappingTemplate: IAccountMappingTemplate | undefined;
    @Input() incomeMappingTemplate: IAccountMappingTemplate | undefined;
    // Raw (pre-G&M) templates feed the SuperTable selection tree so account
    // picking & matching ignores Grouping/Masking (REV-21023). Optional with
    // fallback to the transformed templates above.
    @Input() rawBalanceMappingTemplate: IAccountMappingTemplate | undefined;
    @Input() rawIncomeMappingTemplate: IAccountMappingTemplate | undefined;

    subscriptions: Subscription[] = [];
    notesAccordionData: INotesSectionViewModel[] = [];

    richTextFromInformationUpdate$ = new Subject<{
        informationRichText: INotesComponent;
        componentIndex: number;
        accordionIndex: number;
    }>();

    outputtedAnnualAccordionsData: IAnnualReportAccordionsData = {
        accordions: [],
    };
    outputtedAnnualDefaultAccordionsData: IAnnualReportAccordionsData;
    isBackendReturnError = false;
    isClientReturnError = false;
    backendErrors: string;
    clientErrors: string;
    accordions: HamAccordionItemComponent[] = [];
    resetValuesFromMappingForAllAccordions: object | null = null;
    resetValuesFromMappingForAllComponents: object | null = null;
    resetValuesFromMappingForAllComponentsAccordionIndex: number;
    resetState: boolean[] = [];
    isGlobalResetInProgress = false;
    private isPageInitialized = false;
    /**
     * REV-22548: components the page-level refresh handed new data to and that have not yet reported it
     * back. Only their emits are checked against live data — every other emit (e.g. picking an FLI) goes
     * the normal way, so editing never re-renders a section.
     */
    private readonly componentsAwaitingRefresh = new Set<string>();

    autofillMode = false;
    private receivedNotesFromAdministration: INotesOfAnnualReportDTO;
    notesNumberedTitles: INotesComponentsNumberedTitles[][] | undefined;

    public draggingAccordionIndex: number | null = null;

    public showAddNewNotesAccordionSection = false;

    openAccordions: { [accordionName: string]: boolean } = {};
    locationId: string;
    openAccordions$: Observable<{ [accordionName: string]: boolean }>;
    projectId: string;

    @ViewChildren('accordions') accordionChildren: QueryList<HamAccordionItemComponent>;

    onAccordionExpand(index: number): void {
        this.notesAccordionData[index].isExpanded = true;
    }

    onAccordionClosed(index: number): void {
        this.notesAccordionData[index].isExpanded = false;
    }

    isAccordionHidden(noteAccordion: INotesSectionViewModel): boolean {
        if (!noteAccordion.data || noteAccordion.data.length === 0) {
            return false;
        }
        const validComponents = noteAccordion.data.filter((component) => component != null);
        if (validComponents.length === 0) {
            return false;
        }
        return validComponents.every((component) => component.isHidden === true);
    }

    onToggleAccordionVisibility(accordionIndex: number): void {
        const accordion = this.notesAccordionData[accordionIndex];
        const shouldHide = !this.isAccordionHidden(accordion);
        const components = (accordion.data as NoteComponentViewState[]).filter((component) => component != null);

        // Update outputtedAnnualAccordionsData for each component via the standard change path
        components.forEach((component, componentIndex) => {
            const updatedComponent: NoteComponentViewState = { ...component, isHidden: shouldHide };
            this.onComponentDataChange(updatedComponent, componentIndex, accordionIndex);
        });

        // Replace the data array with a new reference so Angular detects the change
        // and re-triggers the componentData input setter in NotesComponentsListComponent
        this.notesAccordionData[accordionIndex] = {
            ...this.notesAccordionData[accordionIndex],
            data: this.notesAccordionData[accordionIndex].data
                .filter((component) => component != null)
                .map((component) => ({ ...component, isHidden: shouldHide })),
        };
    }

    private updateAccordionsWithDefaultSettings(): void {
        this.notesAccordionData = this.notesAccordionDataSettingsUpdaterHelper.updateCurrencyAndDatesOfAccordions(
            this.annualReportAccordions.accordions,
            this.annualReportDefaultSettings,
            this.flisWithAccounts,
        );

        this.outputtedAnnualAccordionsData = cloneDeep<IAnnualReportAccordionsData>(this.annualReportAccordions);

        this.updateAccordionsArray();

        this.addAccordionsMarkers();

        this.createNotesNumberingObject();
    }

    scrollToAccordion(index: number): void {
        ///Disable scroll to accordion
        return;
        /*const accordionPanel = this.accordionChildren.toArray()[index];
        if (!accordionPanel.expanded) {
            return;
        }
        if (accordionPanel) {
            const scrollableParent = this.findScrollableParent(accordionPanel._body.nativeElement);
            if (scrollableParent) {
                const panelTop = accordionPanel._body.nativeElement.offsetTop - scrollableParent.offsetTop;
                scrollableParent.scrollTo({ top: panelTop - 80, behavior: 'smooth' });
            }
        }*/
    }

    findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;
        while (parent) {
            if (this.isScrollable(parent)) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }

    isScrollable(element: HTMLElement): boolean {
        const overflowY = window.getComputedStyle(element).overflowY;
        return (overflowY === 'scroll' || overflowY === 'auto') && element.scrollHeight > element.clientHeight;
    }

    constructor(
        private provider: NotesProvider,
        private notesHelper: NotesHelper,
        private accordionCompletenessHelper: AccordionCompletenessHelper,
        private notesAutoFillHelper: NotesAutoFillHelper,
        private notesAccordionDataSettingsUpdaterHelper: NotesAccordionDataSettingsUpdaterHelper,
        private mockNotesAccordionDataHelper: MockNotesAccordionDataHelper,
        private i18nextService: I18NextService,
        private actionsListener$: ActionsSubject,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private store: Store,
        private readonly dialog: MatDialog,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly notesNumberingHelper: NotesNumberingHelper,
        private readonly superTableMapper: SuperTableMapper,
        private readonly superTableHelper: SuperTableHelper,
    ) {
        this.applyLocalizations();
        this.addSvgIcons();
        this.handleNotesTypeOfNotesTemplateWithAutofill = this.handleNotesTypeOfNotesTemplateWithAutofill.bind(this);
        this.handleNotesTemplateError = this.handleNotesTemplateError.bind(this);
        this.applyChangeOfAccordion = this.applyChangeOfAccordion.bind(this);

        this.store
            .select(selectActiveProjectId)
            .pipe(untilDestroyed(this))
            .subscribe((projectId) => {
                this.projectId = projectId;
                this.locationId = this.buildLocationId();
                this.openAccordions$ = this.store.pipe(select(selectAccordionOpenState(this.locationId)));
            });

        this.openAccordions$
            .pipe(take(1)) // take(1) to get the current value and auto-unsubscribe
            .subscribe((openState) => {
                if (openState) {
                    this.openAccordions = openState as { [accordionName: string]: boolean };
                }
            });
    }

    private applyLocalizations(): void {
        if (
            !this.isNotesSavedBefore &&
            this.combination &&
            !this.autofillMode &&
            this.receivedNotesFromAdministration != null
        ) {
            this.handleNotesTypeOfNotesTemplateWithAutofill();
        }
        this.createNotesNumberingObject();
    }

    getChangeOfComponent(change: Notes, index: number, componentIndex: number) {
        this.outputtedAnnualAccordionsData.accordions = this.notesHelper.prepareOutputtedAccordionsData(
            change,
            index,
            componentIndex,
            this.outputtedAnnualAccordionsData.accordions,
        );

        if (this.accordions.length !== 0) {
            this.addAccordionMarkers(this.outputtedAnnualAccordionsData.accordions[index], index);
        }

        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
        this.createNotesNumberingObject();
    }

    public ngOnInit(): void {
        debug('ngOnInit');

        if (!this.isNotesSavedBefore) {
            debug('no saved data before');

            if (this.arTemplateNotesTemplateId != null) {
                this.provider.dispatchNotesTemplateById(this.arTemplateNotesTemplateId);

                this.actionsListener$
                    .pipe(ofType(getNotesTemplateById.success), untilDestroyed(this))
                    .subscribe((response) => {
                        if (this._notesFromChosenAnnualReport != null) return;
                        const notesOfAnnualReport: INotesOfAnnualReportDTO = {
                            notes: response.payload.notesTypes,
                        };
                        this.receivedNotesFromAdministration = cloneDeep<INotesOfAnnualReportDTO>(notesOfAnnualReport);
                        this.handleNotesTypeOfNotesTemplateWithAutofill();
                    });
            } else if (this.combination != null) {
                this.provider.dispatchNotesOfAnnualReport(this.combination);

                this.provider
                    .getNotesOfAnnualReport$()
                    .pipe(
                        filter((notes: INotesOfAnnualReportDTO) => notes != null),
                        untilDestroyed(this),
                    )
                    .subscribe((value) => {
                        if (this._notesFromChosenAnnualReport != null) return;
                        this.receivedNotesFromAdministration = cloneDeep<INotesOfAnnualReportDTO>(value);
                        this.handleNotesTypeOfNotesTemplateWithAutofill();
                    });
            }
        } else {
            if (this.annualReportAccordions != null) {
                this.notesAccordionData =
                    this.notesAccordionDataSettingsUpdaterHelper.updateCurrencyAndDatesOfAccordions(
                        this.annualReportAccordions.accordions,
                        this.annualReportDefaultSettings,
                        this.flisWithAccounts,
                    );

                this.outputtedAnnualAccordionsData = cloneDeep<IAnnualReportAccordionsData>(
                    this.annualReportAccordions,
                );
                this.DELEGATE.outputtedAccordionsDefaultData(this.outputtedAnnualDefaultAccordionsData);
            }
        }

        this.provider.getNotesTemplateError$().pipe(untilDestroyed(this)).subscribe(this.handleNotesTemplateError);

        this.isPageInitialized = true;
        this.refreshMappingDerivedComponentsFromLiveData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Live data can arrive after the saved notes (the host binds the notes before the FLIs), so
        // every late arrival re-runs the refresh; it is idempotent and emits only on a real change.
        const liveInputs = [
            'annualReportAccordionsData',
            'flis',
            'balanceMappingTemplate',
            'incomeMappingTemplate',
            'rawBalanceMappingTemplate',
            'rawIncomeMappingTemplate',
            'isSealed',
        ];
        if (liveInputs.some((input) => changes[input] != null)) {
            this.refreshMappingDerivedComponentsFromLiveData();
        }
    }

    /**
     * REV-22548: brings every FLI table, movement schedule and super table of the SAVED notes up to
     * date with live data — in every section, expanded or not — and hands the result to the host as
     * a reset, so one save makes the editor, the saved notes and the print agree and bumps the notes
     * version. Nothing changed upstream → nothing is emitted and the page stays clean.
     */
    private refreshMappingDerivedComponentsFromLiveData(): void {
        if (
            !this.isPageInitialized ||
            !this.isNotesSavedBefore ||
            this.DELEGATE == null ||
            this.outputtedAnnualAccordionsData?.accordions == null
        ) {
            return;
        }

        let refreshed: ReturnType<typeof NotesLiveRefreshHelper.refreshAccordions>;
        try {
            refreshed = NotesLiveRefreshHelper.refreshAccordions(
                this.outputtedAnnualAccordionsData.accordions,
                this.buildNotesLiveData(),
                { isSealed: this.isSealed },
            );
        } catch (error) {
            // A failed refresh must never break the editor: the notes stay as saved.
            console.error('Error in refreshMappingDerivedComponentsFromLiveData:', error);
            return;
        }
        const { accordions, changed } = refreshed;
        if (!changed) {
            return;
        }

        this.outputtedAnnualAccordionsData = { ...this.outputtedAnnualAccordionsData, accordions };

        // The editor shows exactly what will be saved: hand the refreshed data to the rendered components.
        // A NEW section object per changed section — the components list only takes new data when its
        // input changes, so an in-place update would leave an already rendered component on the old data.
        this.notesAccordionData = this.notesAccordionData.map((viewSection, accordionIndex) => {
            const section = accordions[accordionIndex];
            if (section == null || !Array.isArray(viewSection?.data)) {
                return viewSection;
            }
            let sectionChanged = false;
            const data = viewSection.data.map((viewComponent, componentIndex) => {
                const component = section.data?.[componentIndex];
                if (
                    viewComponent == null ||
                    component == null ||
                    viewComponent.componentId !== component.componentId ||
                    isEqual(viewComponent.data, component.data)
                ) {
                    return viewComponent;
                }
                sectionChanged = true;
                if (viewComponent.componentId != null) {
                    this.componentsAwaitingRefresh.add(viewComponent.componentId);
                }
                return { ...viewComponent, data: cloneDeep(component.data) };
            });
            return sectionChanged ? { ...viewSection, data } : viewSection;
        });
        this.notesAccordionData = this.notesAccordionDataSettingsUpdaterHelper.updateCurrencyAndDatesOfAccordions(
            this.notesAccordionData,
            this.annualReportDefaultSettings,
            this.flisWithAccounts,
        );

        this.DELEGATE.outputtedAccordionsData(this.outputtedAnnualAccordionsData, true);
        this.createNotesNumberingObject();
    }

    /** The live data the saved notes are refreshed against: the FLIs plus the mapping selection trees. */
    private buildNotesLiveData(): Parameters<typeof NotesLiveRefreshHelper.refreshAccordions>[1] {
        const balanceTemplate = this.rawBalanceMappingTemplate ?? this.balanceMappingTemplate;
        const incomeTemplate = this.rawIncomeMappingTemplate ?? this.incomeMappingTemplate;
        return {
            flis: this.flisWithAccounts,
            balanceMappingItemsSelectionTree:
                balanceTemplate != null
                    ? this.superTableMapper.buildMappingItemsSelectionTree(
                          balanceTemplate.group,
                          TemplateType.BALANCE,
                          this.fxNotesContext,
                      )
                    : undefined,
            incomeMappingItemsSelectionTree:
                incomeTemplate != null
                    ? this.superTableMapper.buildMappingItemsSelectionTree(incomeTemplate.group, TemplateType.INCOME)
                    : undefined,
        };
    }

    /**
     * REV-22548: a component reports the data it holds. A FLI table, movement schedule or super table
     * that was rendered before the page-level refresh still holds the old mapping (e.g. a Spiegel on an
     * FLI that had no accounts, after an account was mapped onto it) — refreshed here, so the old data is
     * never saved over the refreshed one. Typed values survive the refresh, so edits are kept.
     * Only components the page-level refresh changed are checked, until they report the refreshed data.
     * Returns the refreshed component, or null when nothing differs from live data.
     */
    private refreshEmittedComponent(component: NoteComponentViewState): NoteComponentViewState | null {
        if (
            !this.isPageInitialized ||
            !this.isNotesSavedBefore ||
            this.isSealed ||
            !this.componentsAwaitingRefresh.has(component.componentId)
        ) {
            return null;
        }
        try {
            const { accordions, changed } = NotesLiveRefreshHelper.refreshAccordions(
                [{ data: [component] } as unknown as INotesSection],
                this.buildNotesLiveData(),
                { isSealed: this.isSealed },
            );
            if (!changed) {
                // The component now holds the refreshed data: from here on its emits are the user's edits.
                this.componentsAwaitingRefresh.delete(component.componentId);
                return null;
            }
            return { ...component, data: accordions[0].data[0].data } as NoteComponentViewState;
        } catch (error) {
            console.error('Error in refreshEmittedComponent:', error);
            return null;
        }
    }

    ngAfterViewInit(): void {
        const isAccordionChildrenEmptyCurrently: boolean = this.accordionChildren.toArray().length !== 0;

        if (isAccordionChildrenEmptyCurrently) {
            this.accordions = this.accordionChildren.toArray();
            this.addAccordionsMarkers();
        } else {
            this.detectAccordionsChange();
        }
    }

    handleNotesTypeOfNotesTemplateWithAutofill() {
        const accordionsToBeAutoFilled: INotesSection[] =
            this.notesToBeAutoFilled != null ? this.notesToBeAutoFilled.accordions : undefined;

        debug(accordionsToBeAutoFilled, 'accordionsToBeAutoFilled');
        if (
            accordionsToBeAutoFilled != null &&
            this.balanceMappingTemplate != null &&
            this.incomeMappingTemplate != null
        ) {
            this.autofillMode = true;
            // SuperTable selection tree must use the raw mapping structure (REV-21023).
            const balanceTemplateForSelection = this.rawBalanceMappingTemplate ?? this.balanceMappingTemplate;
            const incomeTemplateForSelection = this.rawIncomeMappingTemplate ?? this.incomeMappingTemplate;
            const balanceMappingItemsSelectionTree: MappingItemsSelectionNode = cloneDeep<MappingItemsSelectionNode>(
                this.superTableMapper.buildMappingItemsSelectionTree(
                    balanceTemplateForSelection.group,
                    TemplateType.BALANCE,
                    this.fxNotesContext,
                ),
            );
            const incomeMappingItemsSelectionTree: MappingItemsSelectionNode = cloneDeep<MappingItemsSelectionNode>(
                this.superTableMapper.buildMappingItemsSelectionTree(
                    incomeTemplateForSelection.group,
                    TemplateType.INCOME,
                ),
            );

            const allMappingItemsSelectionTree: MappingItemsSelectionNode = {
                data: null,
                children: balanceMappingItemsSelectionTree.children.concat(incomeMappingItemsSelectionTree),
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            };

            const notesAccordionsAfterAutoFilling = this.notesAutoFillHelper.autofillAccordions(
                accordionsToBeAutoFilled,
                this.flisWithAccounts,
                allMappingItemsSelectionTree,
            );

            const updatedNotesAccordionData =
                this.notesAccordionDataSettingsUpdaterHelper.updateCurrencyAndDatesOfAccordions(
                    notesAccordionsAfterAutoFilling,
                    this.annualReportDefaultSettings,
                    this.flisWithAccounts,
                );

            const accordionsData: IAnnualReportAccordionsData = {
                accordions: updatedNotesAccordionData,
            };
            this.outputtedAnnualAccordionsData = cloneDeep<IAnnualReportAccordionsData>(accordionsData);

            this.notesAccordionData = cloneDeep<INotesSectionViewModel[]>(updatedNotesAccordionData);

            this.updateAccordionsArray();

            this.addAccordionsMarkers();
            this.receivedNotesFromAdministration = undefined;
        } else if (this.receivedNotesFromAdministration) {
            const preparedAccordions = this.notesHelper.prepareNotesAccordionData(
                this.receivedNotesFromAdministration.notes,
                this.annualReportDefaultSettings,
            );
            this.notesAccordionData = this.mockNotesAccordionDataHelper.mockAccordions(
                preparedAccordions,
                this.i18nextService,
            );
        }

        const accordionsData: IAnnualReportAccordionsData = {
            accordions: this.notesAccordionData,
        };
        this.outputtedAnnualAccordionsData = cloneDeep<IAnnualReportAccordionsData>(accordionsData);
        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
        this.outputtedAnnualDefaultAccordionsData = cloneDeep<IAnnualReportAccordionsData>(accordionsData);
        this.DELEGATE.outputtedAccordionsDefaultData(accordionsData);

        this.provider.resetNotes();

        this.createNotesNumberingObject();
    }

    handleNotesTemplateError(error: HttpErrorResponse) {
        if (!error) {
            this.isBackendReturnError = false;
            this.isClientReturnError = false;
        }
        debug(error);

        if (error) {
            this.prepareErrorMessages(error);
        }
    }

    onEditAccordionTitle(newTitle: string, noteAccordion: INotesSectionViewModel, index: number): void {
        const updateAccordion: INotesSectionViewModel = noteAccordion;

        updateAccordion.title[this.reportLocale] = newTitle;
        updateAccordion.isEditing = false;

        this.outputtedAnnualAccordionsData.accordions[index] = omit(updateAccordion, 'isEditing') as INotesSection;
        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
    }

    onDeleteAccordionClick(accordionIndex: number): void {
        const data: IDialogDataWithAction = {
            contentMessage: 'aumico_confirmation_modal_notes_deletion',
            titleLocalizationKey: undefined,
            actionBtnTextKey: 'aumico_confirmation_modal_delete_button',
        };
        const dialog = this.dialog.open(ConfirmationModal, {
            width: '400px',
            data,
        });

        dialog
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result: OkCancelDialogActions) => {
                if (result === OkCancelDialogActions.OK) {
                    this.notesAccordionData.splice(accordionIndex, 1);

                    this.outputtedAnnualAccordionsData.accordions.splice(accordionIndex, 1);

                    this.DELEGATE.outputtedAccordionsData(
                        this.outputtedAnnualAccordionsData,
                        this.resetState.filter((state) => state === true).length > 0,
                    );

                    this.createNotesNumberingObject();

                    this.updateAccordionsArray();
                }
            });
    }

    drop(event: CdkDragDrop<INotesSectionViewModel[]>) {
        moveItemInArray(this.notesAccordionData, event.previousIndex, event.currentIndex);

        moveItemInArray(this.outputtedAnnualAccordionsData.accordions, event.previousIndex, event.currentIndex);

        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );

        this.updateAccordionsArray();

        this.createNotesNumberingObject();
    }

    onInformationChange(information: string, saveToRichText: boolean, noteAccordionIndex: number): void {
        this.outputtedAnnualAccordionsData.accordions[noteAccordionIndex].information[this.currentLanguage] =
            information;
        this.notesAccordionData[noteAccordionIndex].information[this.currentLanguage] = information;

        if (saveToRichText) {
            this.updateRichText(information, noteAccordionIndex);
        }

        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
    }

    public openAddNewNotesAccordionSection(): void {
        this.showAddNewNotesAccordionSection = true;
    }

    addNewAccordion(noteAccordion: AddNoteAccordionResult): void {
        const accordionIndex = this.notesAccordionData.length ? this.notesAccordionData.length - 1 : 0;

        let noteComponent: INotesComponent | undefined;

        // Using Pasted Component
        if (noteAccordion.pastedComponent != null) {
            const balanceFlis = getAllFlis(this.balanceMappingTemplate.group);
            const incomeFlis = getAllFlis(this.incomeMappingTemplate.group);
            const allFlis = balanceFlis.concat(incomeFlis);

            if (noteAccordion.pastedComponent.componentType === ComponentType.SUPER_TABLE) {
                noteAccordion.pastedComponent.component.data.currentDate =
                    this.annualReportDefaultSettings.currentBalanceDate;
                noteAccordion.pastedComponent.component.data.previousDate =
                    this.annualReportDefaultSettings.previousBalanceDate;
                noteComponent = this.notesHelper.copySuperTableComponentStructureWithData(
                    noteAccordion.pastedComponent.component,
                    allFlis,
                    this.balanceMappingTemplate,
                    this.incomeMappingTemplate,
                    false,
                );
            }
            if (noteAccordion.pastedComponent.componentType === ComponentType.FREE_TABLE_WITH_AMOUNTS) {
                noteAccordion.pastedComponent.component.data.currentDate =
                    this.annualReportDefaultSettings.currentBalanceDate;
                noteAccordion.pastedComponent.component.data.previousDate =
                    this.annualReportDefaultSettings.previousBalanceDate;
                noteComponent = this.notesHelper.copyFreeTableWithAmountsComponentStructureWithData(
                    noteAccordion.pastedComponent.component,
                    allFlis,
                    false,
                );
            }
            // Creating Empty Component of Selected Type
        } else {
            const componentId = NotesHelper.generateComponentId(noteAccordion.selectedComponent);

            const emptyNoteComponentData = this.notesHelper.createEmptyNoteComponent(
                noteAccordion.pastedComponent != null
                    ? noteAccordion.pastedComponent.componentType
                    : noteAccordion.selectedComponent,
                this.annualReportDefaultSettings,
                this.languages,
                this.fxNotesContext != null,
            );

            noteComponent = {
                componentId,
                componentName: noteAccordion.selectedComponent,
                data: emptyNoteComponentData,
                hasFootnotes: false,
            };
        }

        this.notesAccordionData.unshift({
            name: `notes_accordion_${accordionIndex}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
            title: {
                [this.reportLocale]: noteAccordion.title,
            },
            data: [noteComponent],
            information: {
                [this.reportLocale]: EMPTY_STRING,
            },
        });

        if (this.outputtedAnnualAccordionsData === undefined) {
            //Initialize accordions first
            this.initAccordions();
        }
        this.outputtedAnnualAccordionsData.accordions.unshift(cloneDeep(this.notesAccordionData[0]));

        this.updateAccordionsArray();

        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
        this.createNotesNumberingObject();
        this.showAddNewNotesAccordionSection = false;
    }

    private initAccordions() {
        this.accordions = [];
        this.annualReportAccordions = { accordions: [] };
        this.outputtedAnnualAccordionsData = { accordions: [] };
        this.notesAccordionData = [];
    }

    public closeAddNewNotesAccordionSection(): void {
        this.showAddNewNotesAccordionSection = false;
    }

    private updateRichText(information: string, accordionIndex: number): void {
        const accordionToUpdate = this.outputtedAnnualAccordionsData.accordions[accordionIndex];

        const richTextComponentIndex = accordionToUpdate.data.findIndex(
            (component) => component.componentName === ComponentType.RICH_TEXT,
        );

        if (richTextComponentIndex > -1) {
            const component = accordionToUpdate.data[richTextComponentIndex];
            const richTextIndex = (component.data as IRichText[]).findIndex(
                (richText) => richText.language === this.reportLocale,
            );
            const newData = { ...accordionToUpdate.data[richTextComponentIndex] };
            newData.data[richTextIndex].text = information;

            accordionToUpdate.data[richTextComponentIndex] = newData;

            this.onComponentDataChange(
                accordionToUpdate.data[richTextComponentIndex],
                richTextComponentIndex,
                accordionIndex,
            );

            this.richTextFromInformationUpdate$.next({
                informationRichText: accordionToUpdate.data[richTextComponentIndex],
                componentIndex: richTextComponentIndex,
                accordionIndex,
            });
        } else {
            const newRichText = this.notesHelper.createEmptyNoteComponent(
                ComponentType.RICH_TEXT,
                this.annualReportDefaultSettings,
                this.languages,
            );
            const richTextOfReportLanguage = (newRichText as IRichText[]).find(
                (richText) => richText.language === this.reportLocale,
            );
            richTextOfReportLanguage.text = information;

            const componentId = NotesHelper.generateComponentId(ComponentType.RICH_TEXT);
            accordionToUpdate.data.push({
                componentId,
                componentName: ComponentType.RICH_TEXT,
                data: newRichText,
                hasFootnotes: false,
            });

            const index = accordionToUpdate.data.length - 1;

            this.onComponentDataChange(accordionToUpdate.data[index], index, accordionIndex);

            this.richTextFromInformationUpdate$.next({
                informationRichText: accordionToUpdate.data[index],
                componentIndex: index,
                accordionIndex,
            });
        }
    }

    private prepareErrorMessages(error: HttpErrorResponse) {
        switch (error.status) {
            case 404:
                this.isClientReturnError = true;
                this.clientErrors = Errors.Not_Found;
                break;
            case 500:
                this.isBackendReturnError = true;
                this.backendErrors = Errors.ServerError;
                break;
            default:
                this.isBackendReturnError = true;
                this.backendErrors = Errors.ServerError;
        }
    }

    private addAccordionsMarkers(): void {
        if (this.outputtedAnnualAccordionsData != null) {
            this.outputtedAnnualAccordionsData.accordions.forEach((accordion, index) => {
                this.addAccordionMarkers(accordion, index);
            });
        }
    }

    public addAccordionMarkers(accordion: INotesSection, index: number): void {
        // Accordion Completeness
        if (this.accordions[index] === undefined) {
            return;
        }

        this.accordions[index].filled = this.accordionCompletenessHelper.isAccordionCompleted(
            accordion,
            this.reportLocale,
        );
        this.notesAccordionData[index].filled = this.accordions[index].filled;

        // Accordion Correctness(Reconcilation)

        if (this.balanceMappingTemplate == null || this.incomeMappingTemplate == null) {
            return;
        }
        // Reconciliation must match against the same tree the SuperTable picker uses,
        // so it also runs against the raw mapping structure (REV-21023).
        const balanceTemplateForSelection = this.rawBalanceMappingTemplate ?? this.balanceMappingTemplate;
        const incomeTemplateForSelection = this.rawIncomeMappingTemplate ?? this.incomeMappingTemplate;
        const balanceMappingItemsSelectionTree: MappingItemsSelectionNode = cloneDeep<MappingItemsSelectionNode>(
            this.superTableMapper.buildMappingItemsSelectionTree(
                balanceTemplateForSelection.group,
                TemplateType.BALANCE,
                this.fxNotesContext,
            ),
        );
        const incomeMappingItemsSelectionTree: MappingItemsSelectionNode = cloneDeep<MappingItemsSelectionNode>(
            this.superTableMapper.buildMappingItemsSelectionTree(incomeTemplateForSelection.group, TemplateType.INCOME),
        );

        const allMappingItemsSelectionTree: MappingItemsSelectionNode = {
            data: null,
            children: balanceMappingItemsSelectionTree.children.concat(incomeMappingItemsSelectionTree),
            type: ItemType.GROUP,
            financialType: null,
            expanded: true,
        };

        let hasReconcilation = false;
        let isCorrect: boolean | null = null;

        outerLoop: for (const component of accordion.data) {
            if (component.componentName === 'Super Table' && component.data != null) {
                for (const row of (component.data as ISuperTableComponent).rows) {
                    if (
                        row.reconcileItems != null &&
                        row.reconcileItems.length > 0 &&
                        ((component.data.options as ISuperTableOptions).compareYears ||
                            !(component.data.options as ISuperTableOptions).hideCurrentYear)
                    ) {
                        hasReconcilation = true;
                        const matchingNodes = this.superTableMapper.findMatchingNodesForReconcileItems(
                            row.reconcileItems,
                            allMappingItemsSelectionTree,
                        );

                        const reconcilationMisMatchResult = this.superTableHelper.getReconcilationMisMatchResult(
                            matchingNodes,
                            row,
                        );

                        if (
                            (reconcilationMisMatchResult.reconcilationCurrentYearMismatch &&
                                !(component.data.options as ISuperTableOptions).hideCurrentYear) ||
                            (reconcilationMisMatchResult.reconcilationPreviousYearMismatch &&
                                (component.data.options as ISuperTableOptions).compareYears)
                        ) {
                            isCorrect = false;
                            break outerLoop;
                        } else {
                            // Row is reconciled correctly; keep going — a later row/component may still mismatch
                            isCorrect = true;
                        }
                    }
                }
            }
        }

        const finalCorrect = hasReconcilation ? isCorrect : null;
        this.accordions[index].isCorrect = finalCorrect;
        this.notesAccordionData[index].isCorrect = finalCorrect;
    }

    getAccordionIndex(noteAccordion: INotesSectionViewModel): string | number | null {
        if (!noteAccordion.filled) return '-';
        const filledNotes = this.notesAccordionData.filter((n) => n.filled);
        return filledNotes.indexOf(noteAccordion) + 1;
    }

    private detectAccordionsChange() {
        this.accordionChildren.changes.pipe(untilDestroyed(this)).subscribe(this.applyChangeOfAccordion);
        this.createNotesNumberingObject();
    }

    private applyChangeOfAccordion(accordionsChange: HamAccordionItemComponent[]) {
        accordionsChange.forEach((item: HamAccordionItemComponent) => {
            this.accordions.push(item);
        });

        if (this.accordions.length !== 0) {
            this.addAccordionsMarkers();
        }
    }

    private addSvgIcons(): void {
        this.matIconRegistry
            .addSvgIcon(
                'aumico-pattern-icon-edit-notes',
                this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/aumico-pattern-icon-edit-notes.svg'),
            )
            .addSvgIcon(
                'aumico-pattern-icon-drag-panel',
                this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/aumico-pattern-icon-drag-panel.svg'),
            )
            .addSvgIcon(
                'aumico-pattern-icon-bin',
                this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/aumico-pattern-icon-bin.svg'),
            )
            .addSvgIcon(
                'aumico-pattern-icon-round-back',
                this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/aumico-pattern-icon-round-back.svg'),
            )
            .addSvgIcon(
                'aumico-pattern-icon-information',
                this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/aumico-pattern-icon-information.svg'),
            );
    }

    onDrag(index: number): void {
        if (this.accordionChildren.get(index).expanded) {
            this.accordionChildren.get(index).expanded = false;
        }
    }

    onComponentDataChange(component: NoteComponentViewState, componentIndex: number, noteAccordionIndex: number): void {
        const refreshedComponent = this.refreshEmittedComponent(component);
        if (refreshedComponent != null) {
            // The rendered component held old data: hand it the refreshed data through a NEW section
            // object (the components list ignores in-place changes), so the editor shows what is saved.
            component = refreshedComponent;
            const viewSection = this.notesAccordionData[noteAccordionIndex];
            const data = [...viewSection.data];
            data[componentIndex] = { ...component };
            this.notesAccordionData[noteAccordionIndex] = { ...viewSection, data };
        } else {
            this.notesAccordionData[noteAccordionIndex].data[componentIndex] = { ...component };
        }

        const acc = this.outputtedAnnualAccordionsData.accordions[noteAccordionIndex];
        const arr = acc.data;

        // Drop view-only fields
        const newComponent = omit(component, ['state', 'isSaveButtonClicked']);

        // Find if this component already exists (update) or is truly new (insert)
        const id = (component as any).componentId;
        const existingIdx = arr.findIndex((c: any) => c?.componentId === id);

        // Clamp the target index
        const targetIdx = Math.min(Math.max(componentIndex, 0), arr.length);

        if (existingIdx === -1) {
            // New component → INSERT (do not overwrite!)
            arr.splice(targetIdx, 0, newComponent);
        } else if (existingIdx === targetIdx) {
            // Same position → REPLACE contents (update)
            arr[existingIdx] = newComponent;
        } else {
            // Component exists but moved → MOVE to target index
            const [item] = arr.splice(existingIdx, 1);
            arr.splice(
                // if removing from before target, target shifts left by 1
                existingIdx < targetIdx ? targetIdx - 1 : targetIdx,
                0,
                newComponent,
            );
        }

        this.outputtedAnnualAccordionsData.accordions[noteAccordionIndex] = {
            ...acc,
            data: [...arr],
        };

        this.addAccordionsMarkers();
        const notesSections = this.notesHelper.mapINotesAccordionsToINotesSections(
            this.outputtedAnnualAccordionsData.accordions,
        );
        this.outputtedAnnualAccordionsData.accordions = notesSections;

        if (this.DELEGATE) {
            this.DELEGATE.outputtedAccordionsData(
                this.outputtedAnnualAccordionsData,
                this.resetState.filter((state) => state === true).length > 0,
            );
        }

        this.createNotesNumberingObject();
    }

    onDeleteComponent(componentIndex: number, accordionIndex: number): void {
        if (this.outputtedAnnualAccordionsData.accordions[accordionIndex].data != null) {
            this.outputtedAnnualAccordionsData.accordions[accordionIndex].data.splice(componentIndex, 1);
            this.notesAccordionData[accordionIndex].data.splice(componentIndex, 1);

            this.addAccordionsMarkers();
            this.DELEGATE.outputtedAccordionsData(
                this.outputtedAnnualAccordionsData,
                this.resetState.filter((state) => state === true).length > 0,
            );
            this.createNotesNumberingObject();
        }
    }

    onDropComponent(previousIndex: number, currentIndex: number, accordionIndex: number): void {
        if (this.outputtedAnnualAccordionsData.accordions[accordionIndex].data != null) {
            moveItemInArray(
                this.outputtedAnnualAccordionsData.accordions[accordionIndex].data,
                previousIndex,
                currentIndex,
            );

            // Keep notesAccordionData in sync so that onToggleAccordionVisibility
            // iterates components in the current order and does not reset positions.
            if (this.notesAccordionData[accordionIndex]?.data != null) {
                moveItemInArray(
                    this.notesAccordionData[accordionIndex].data,
                    previousIndex,
                    currentIndex,
                );
            }

            this.DELEGATE.outputtedAccordionsData(
                this.outputtedAnnualAccordionsData,
                this.resetState.filter((state) => state === true).length > 0,
            );
            this.createNotesNumberingObject();
        }
    }

    private updateAccordionsArray(): void {
        this.changeDetector.detectChanges();
        this.accordions = this.accordionChildren.toArray();
    }

    public onHighlightTodos(highlightTodos: boolean): void {
        this.highlightTodos = highlightTodos;
        this.highlightTodosChanged.emit(highlightTodos);
    }

    onTableReferenceChange(
        data: Pick<Notes, 'addNumbering' | 'referenceFlis'>,
        componentIndex: number,
        tableIndex: number,
        accordionIndex: number,
    ): void {
        if (
            this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex].componentName ===
            ComponentType.FINANCIAL_LINE_ITEM ||
            this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex].componentName ===
            ComponentType.MOVEMENT_SCHEDULE
        ) {
            this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex].data.flis[tableIndex] = {
                ...this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex].data.flis[
                tableIndex
                ],
                addNumbering: data.addNumbering,
            };
        } else {
            this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex].data = {
                ...this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex].data,
                addNumbering: data.addNumbering,
                referenceFlis: data.referenceFlis,
            };
        }
        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
        this.createNotesNumberingObject();
    }

    onComponentReferenceChange(
        data: Pick<INotesComponent, 'addNumbering' | 'referenceFlis'>,
        componentIndex: number,
        accordionIndex: number,
    ): void {
        this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex] = {
            ...this.outputtedAnnualAccordionsData.accordions[accordionIndex].data[componentIndex],
            addNumbering: data.addNumbering,
            referenceFlis: data.referenceFlis,
        };

        this.DELEGATE.outputtedAccordionsData(
            this.outputtedAnnualAccordionsData,
            this.resetState.filter((state) => state === true).length > 0,
        );
        this.createNotesNumberingObject();
    }

    public onCopyNotesStructureFromAnotherAnnualReportButtonClicked(): void {
        this.isCopyNotesStructureFromAnotherAnnualReportButtonClicked = true;
        this.copyNotesStructureFromAnotherAnnualReportButtonClicked.emit();
    }

    onRowInModalSelected(selectedAnnualReportId: string): void {
        this.selectedAnnualReportIdToCopyNotesStructureFrom = selectedAnnualReportId;
    }

    onCopyNotesStructureFromAnotherAnnualReportSaveButtonClicked() {
        this.selectedAnnualReportIdToCopyNotesStructureFromEvent.emit(
            this.selectedAnnualReportIdToCopyNotesStructureFrom,
        );
        this.isCopyNotesStructureFromAnotherAnnualReportButtonClicked = false;
    }

    onInformationIconClicked(event: Event, noteAccordion: any, accordionIndex: number): void {
        event.stopPropagation();

        const informationText = noteAccordion.information;

        const popupData: IPopupData = {
            titleLocalizationKey: 'aumico_core_accordion_Information_dialog_title',
            isContentHtmlType: true,
            isContentEditable: true,
            contentMessage: informationText[this.currentLanguage],
        };

        const dialogRef = this.dialog.open<
            AumicoEditableInformationPopupComponent,
            IPopupData,
            { information: string; saveToRichText: boolean }
        >(AumicoEditableInformationPopupComponent, {
            width: '600px',
            data: popupData,
            disableClose: true,
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result != null) {
                noteAccordion.information[this.currentLanguage] = result.information;

                this.onInformationChange(result.information, result.saveToRichText, accordionIndex);
            }
        });
    }

    onDragStarted(event: CdkDragStart, index: number): void {
        const element = event.source.element.nativeElement;
        this.draggingAccordionIndex = index;
        this.changeDetector.detectChanges();
        element.classList.add('dragging');
    }

    onDragEnded(event: CdkDragEnd): void {
        const element = event.source.element.nativeElement;
        this.draggingAccordionIndex = null;
        element.classList.remove('dragging');
    }

    private createNotesNumberingObject(): void {
        if (this.outputtedAnnualAccordionsData == null) {
            return;
        }
        const notesAccordionswithNumbering = this.notesNumberingHelper.applyNumbering(
            this.outputtedAnnualAccordionsData.accordions,
            this.reportLocale,
            true,
        );
        this.notesNumberedTitles = this.notesNumberingHelper.createNumberingObject(
            notesAccordionswithNumbering,
            this.reportLocale,
        );
    }

    /**
     * Toggle open/close state for a notes accordion
     */
    toggleAccordion(accordionName: string, enforce?: boolean) {
        // All accordions are closed, but one. So if we open an accordion, the others must be closed.
        for (const [name, isOpen] of Object.entries(this.openAccordions)) {
            if (isOpen && name !== accordionName) {
                this.openAccordions[name] = false;
            }
        }

        const isOpen = !this.openAccordions[accordionName] || enforce;
        this.openAccordions[accordionName] = isOpen;
        this.locationId = this.buildLocationId();
        this.store.dispatch(
            setNotesAccordionOpenState({ locationId: this.locationId, accordionName: accordionName, isOpen }),
        );
    }

    private buildLocationId(): string {
        return `${this.projectId || 'unknown'}__NOTES_ACCORDIONS`;
    }


    onNotesSectionReferencingButtonClick(accordionIndex: number): void {
        const refDialog = this.dialog.open(ReferencingNotesComponent, {
            data: {
                flis: this.flisWithAccounts,
                selectedFlis: this.notesAccordionData[accordionIndex].referenceFlis,
            } as ReferencingNotesComponentData,
            width: '1000px',
            height: '600px',
            panelClass: 'notes-reference-container',
        });

        refDialog
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result: IFLIForNotes[]) => {
                if (!result) return;

                const referenceFlisIds = result.map((fli) => fli.id);

                this.notesAccordionData[accordionIndex].referenceFlis = referenceFlisIds;

                this.outputtedAnnualAccordionsData.accordions[accordionIndex] = {
                    ...this.outputtedAnnualAccordionsData.accordions[accordionIndex],
                    referenceFlis: referenceFlisIds,
                };

                this.DELEGATE.outputtedAccordionsData(
                    this.outputtedAnnualAccordionsData,
                    this.resetState.filter((state) => state === true).length > 0,
                );

            });
    }
}
